import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useState } from "react";
import { useNotify, useTranslate } from "react-admin";
import { useLocation } from "react-router-dom";

import {
  AdminPortalFeedbackCategory,
  SubmitAdminPortalFeedbackDocument,
  SubmitAdminPortalFeedbackMutation,
  SubmitAdminPortalFeedbackMutationVariables,
} from "../generated/graphql";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { useLocationFilterContext } from "../hooks/useLocationFilterContext";
import { useOrganizationContext } from "../hooks/useOrganizationContext";
import { client } from "../lib/apolloClient";
import { color, typography } from "../theme/designTokens";
import { dialogContentSx, dialogPaperSx, dialogTitleSx } from "../theme/dialogStyles";

const MESSAGE_LIMIT = 4000;

const FEEDBACK_CATEGORIES = [
  AdminPortalFeedbackCategory.Bug,
  AdminPortalFeedbackCategory.Idea,
  AdminPortalFeedbackCategory.Question,
  AdminPortalFeedbackCategory.Other,
];

enum BrowserName {
  Chrome = "Chrome",
  Edge = "Edge",
  Firefox = "Firefox",
  Safari = "Safari",
}

enum OperatingSystem {
  Android = "Android",
  ChromeOs = "ChromeOS",
  Ios = "iOS",
  Linux = "Linux",
  MacOs = "macOS",
  Windows = "Windows",
}

const getBrowser = (userAgent: string): BrowserName | undefined => {
  if (userAgent.includes("Edg/")) return BrowserName.Edge;
  if (userAgent.includes("Firefox/")) return BrowserName.Firefox;
  if (userAgent.includes("Chrome/") || userAgent.includes("CriOS/")) return BrowserName.Chrome;
  if (userAgent.includes("Safari/")) return BrowserName.Safari;
  return undefined;
};

const getOperatingSystem = (userAgent: string): OperatingSystem | undefined => {
  if (userAgent.includes("Windows NT")) return OperatingSystem.Windows;
  if (userAgent.includes("Android")) return OperatingSystem.Android;
  if (/iPhone|iPad|iPod/.test(userAgent)) return OperatingSystem.Ios;
  if (userAgent.includes("Mac OS X")) return OperatingSystem.MacOs;
  if (userAgent.includes("CrOS")) return OperatingSystem.ChromeOs;
  if (userAgent.includes("Linux")) return OperatingSystem.Linux;
  return undefined;
};

type FeedbackDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const FeedbackDialog = ({ open, onClose }: FeedbackDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const handleError = useErrorHandler();
  const location = useLocation();
  const { organizationId } = useOrganizationContext();
  const { locationId } = useLocationFilterContext();
  const [category, setCategory] = useState<AdminPortalFeedbackCategory | "">("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = useCallback(() => {
    setCategory("");
    setMessage("");
  }, []);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    reset();
    onClose();
  }, [isSubmitting, onClose, reset]);

  const handleCategoryChange = useCallback((event: SelectChangeEvent<AdminPortalFeedbackCategory | "">) => {
    setCategory(event.target.value as AdminPortalFeedbackCategory);
  }, []);

  const handleMessageChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!category || !message.trim() || message.length > MESSAGE_LIMIT) return;

      setIsSubmitting(true);
      try {
        const result = await client.mutate<
          SubmitAdminPortalFeedbackMutation,
          SubmitAdminPortalFeedbackMutationVariables
        >({
          mutation: SubmitAdminPortalFeedbackDocument,
          variables: {
            input: {
              category,
              message: message.trim(),
              organizationId: organizationId || undefined,
              locationId: organizationId ? locationId || undefined : undefined,
              pagePath: location.pathname,
              portalVersion: process.env.REACT_APP_VERSION || undefined,
              browser: getBrowser(window.navigator.userAgent),
              operatingSystem: getOperatingSystem(window.navigator.userAgent),
            },
          },
        });

        if (!result.data?.submitAdminPortalFeedback?.feedbackId) {
          throw new Error("submitAdminPortalFeedback returned no feedback ID");
        }

        notify("feedback.success", { type: "success" });
        reset();
        onClose();
      } catch (error) {
        handleError(error, translate("feedback.error"), { operation: "submitAdminPortalFeedback" });
      } finally {
        setIsSubmitting(false);
      }
    },
    [category, handleError, location.pathname, locationId, message, notify, onClose, organizationId, reset, translate]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
      data-testid="feedback-dialog"
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            ...dialogTitleSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold }}>
            {translate("feedback.title")}
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={isSubmitting}
            size="small"
            aria-label={translate("ra.action.close")}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ ...dialogContentSx, display: "grid", gap: 3 }}>
          <Typography variant="body2">{translate("feedback.description")}</Typography>

          <Alert severity="info">{translate("feedback.privacyGuidance")}</Alert>

          <FormControl fullWidth required>
            <InputLabel id="feedback-category-label">{translate("feedback.category.label")}</InputLabel>
            <Select
              labelId="feedback-category-label"
              label={translate("feedback.category.label")}
              value={category}
              onChange={handleCategoryChange}
              disabled={isSubmitting}
              data-testid="feedback-category"
            >
              {FEEDBACK_CATEGORIES.map(feedbackCategory => (
                <MenuItem key={feedbackCategory} value={feedbackCategory}>
                  {translate(`feedback.category.${feedbackCategory.toLowerCase()}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            required
            fullWidth
            multiline
            minRows={5}
            label={translate("feedback.message.label")}
            placeholder={translate("feedback.message.placeholder")}
            value={message}
            onChange={handleMessageChange}
            disabled={isSubmitting}
            inputProps={{ maxLength: MESSAGE_LIMIT }}
            helperText={translate("feedback.message.counter", { count: message.length, max: MESSAGE_LIMIT })}
            data-testid="feedback-message"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button color="secondary" onClick={handleClose} disabled={isSubmitting} data-testid="feedback-cancel-button">
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !category || !message.trim() || message.length > MESSAGE_LIMIT}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{ backgroundColor: color.primary, color: color.white }}
            data-testid="feedback-submit-button"
          >
            {translate(isSubmitting ? "feedback.submitting" : "feedback.submit")}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
